import { useState } from "react";
import GenericPopupStyle from "../css/generic-popup.module.css";
import { getMedicationsByICD10, getMedicationBySku } from "../../../../api/dispensary/script/medicationCardApi.js";

export default function useGenericSubstitution({ medications, setMedications }) {
  const [showPopup, setShowPopup] = useState(false);
  const [popupData, setPopupData] = useState([]);
  const [savingMedId, setSavingMedId] = useState(null);

  const openForMedication = async (id) => {
    setSavingMedId(id);
    const med = medications.find((m) => m.id === id);
    const code = med?.icd10;
    try {
      const list = code ? await getMedicationsByICD10(code) : [];
      const mapped = (list || []).map((g) => {
        const cpRaw = g?.costPrice;
        const num = Number(cpRaw);
        const cp = cpRaw !== undefined && cpRaw !== null && cpRaw !== ""
          ? (Number.isFinite(num) ? `R${num.toFixed(2)}` : `R${cpRaw}`)
          : "R0.00";
        return {
          drugName: g?.name || "",
          costPrice: cp,
          sku: g?.sku,
          nappiCode: g?.nappiCode,
          packSize: g?.packSize,
          strength: g?.strength,
          icd10: Array.isArray(g?.icd10) ? g.icd10 : (g?.icd10 ? [g.icd10] : []),
        };
      });
      const currentSku = med?.sku;
      const currentName = (med?.drugName || '').toString().trim().toLowerCase();
      const filtered = mapped.filter((row) => {
        const name = (row.drugName || '').toString().trim().toLowerCase();
        if (currentSku && row.sku) return row.sku !== currentSku;
        return name !== currentName;
      });
      const parsePrice = (p) => {
        if (p == null) return Number.POSITIVE_INFINITY;
        const s = String(p).replace(/[^0-9.]/g, '');
        const n = Number(s);
        return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY;
      };
      filtered.sort((a, b) => parsePrice(a.costPrice) - parsePrice(b.costPrice));
      setPopupData(filtered);
      setShowPopup(true);
    } catch (e) {
      console.error("Failed to fetch generics by ICD10", e);
      setPopupData([]);
      setShowPopup(true);
    }
  };

  const closePopup = () => {
    setShowPopup(false);
    setSavingMedId(null);
  };

  const replaceWithSku = async (row) => {
    if (!savingMedId || !row?.sku) {
      closePopup();
      return;
    }
    try {
      const full = await getMedicationBySku(row.sku);
      const cpRaw = full?.costPrice;
      let cpFormatted = "";
      if (cpRaw !== null && cpRaw !== undefined && cpRaw !== "") {
        const num = Number(cpRaw);
        cpFormatted = Number.isFinite(num) ? `R${num.toFixed(2)}` : `R${cpRaw}`;
      }
      const qRaw = full?.quantity;
      let stockQty;
      let stockText = "";
      if (qRaw !== null && qRaw !== undefined && qRaw !== "") {
        const qNum = Number(qRaw);
        stockQty = Number.isFinite(qNum) ? qNum : undefined;
        const qDisplay = Number.isFinite(qNum) ? (Number.isInteger(qNum) ? `${qNum}` : qNum.toFixed(2)) : `${qRaw}`;
        stockText = `${qDisplay} units in stock`;
      }
      setMedications((prev) => prev.map((m) => (
        m.id === savingMedId
          ? {
              ...m,
              sku: full?.sku || row.sku || m.sku,
              drugName: full?.name || row.drugName || m.drugName,
              nappiCode: full?.nappiCode || row.nappiCode || m.nappiCode,
              packSize: full?.packSize || row.packSize || m.packSize,
              strength: full?.strength || row.strength || m.strength,
              costPrice: cpFormatted || row.costPrice || m.costPrice,
              icd10Options: Array.isArray(full?.icd10) ? full.icd10 : (Array.isArray(row?.icd10) ? row.icd10 : (Array.isArray(m.icd10Options) ? m.icd10Options : [])),
              icd10: (Array.isArray(full?.icd10) ? full.icd10[0] : full?.icd10) || (Array.isArray(row?.icd10) ? row.icd10[0] : row.icd10) || m.icd10,
              quantity: m.quantity,
              stockQty: stockQty !== undefined ? stockQty : undefined,
              stockText: stockText,
            }
          : m
      )));
    } catch (e) {
      console.error("Failed to fetch full medication by SKU", e);
      // fallback: replace from row only
      setMedications((prev) => prev.map((m) => (
        m.id === savingMedId
          ? {
              ...m,
              sku: row.sku || m.sku,
              drugName: row.drugName || m.drugName,
              nappiCode: row.nappiCode || m.nappiCode,
              packSize: row.packSize || m.packSize,
              strength: row.strength || m.strength,
              costPrice: row.costPrice || m.costPrice,
              icd10Options: Array.isArray(row?.icd10) ? row.icd10 : (Array.isArray(m.icd10Options) ? m.icd10Options : []),
              icd10: (Array.isArray(row?.icd10) ? row.icd10[0] : row.icd10) || m.icd10,
              quantity: m.quantity,
              stockQty: undefined,
              stockText: "",
            }
          : m
      )));
    } finally {
      closePopup();
    }
  };

  return {
    // state
    showPopup,
    popupData,
    savingMedId,
    // actions
    openForMedication,
    replaceWithSku,
    closePopup,
    // helpers for UI (optional class for replace button)
    replaceButtonClass: GenericPopupStyle.replaceButton,
  };
}
